import type React from 'react';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  styled,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Button,
  LinearProgress,
  Typography,
  Snackbar,
  Alert,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  type GridCellParams,
  type GridColDef,
  type GridRowParams,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';

import type { TripLegAttestationItem } from '../../../data/models/trip-leg.model';
import {
  TripLegStatus,
  TripLegStatusBC,
  tripStatusCompletedList,
} from '../../../data/models/trip-leg-status.model';
import type { PagedList } from '../../../data/models/paged-list.model';

import { selectTimeZones } from '../../../data/reference.slice';
import {
  selectUpdatingTripLegLogStatus,
  selectUpdatingTripLegLogID,
  updateTripLegLogAsync,
  setTripLegError,
  selectTripLegError,
  setTripLegPriceCalculationError,
  selectTripLegPriceCalculationError,
  overrideTripLegStatus,
  removeTripLegLogEntry,
} from '../../../data/trip.slice';

import { DataGridPro } from '../../tables/DataGridPro';
import { CellDualValue } from '../../tables/CellDualValue';
import { CellEditableField } from '../../tables/CellEditableField';
import { TimeFieldCell } from '../../tables/TimeFieldCell';
import PriceTooltip from '../status/PriceTooltipAsync';

import { StatusDialog } from '../../dialogs/StatusDialog';
import { DateTime } from 'luxon';
import { tripLegStatusBCToStatusMap } from '../../../utils/tripLegStatusMap';
import { useIsInvalidNoLoadTime } from '../../../hooks/useIsInvalidNoLoadTIme';
import { useOptimisticUpdates } from '../../../hooks/useOptimisticUpdates';
import { transformTripLegToRow } from '../../../utils/tripLegRowTransformer';
import { TripLegSearchResponseItem } from '../../../data/models/trip-leg-search.model';

const PREFIX = 'AttestTripsTable';

const classes = {
  content: `${PREFIX}-content`,
  gridContainer: `${PREFIX}-gridContainer`,
  grid: `${PREFIX}-grid`,
  closeButton: `${PREFIX}-closeButton`,
  rowHover: `${PREFIX}-rowHover`,
  cellContent: `${PREFIX}-cellContent`,
  primaryText: `${PREFIX}-primaryText`,
  secondaryText: `${PREFIX}-secondaryText`,
  actions: `${PREFIX}-actions`,
  renderTime: `${PREFIX}-renderTime`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  [`& .${classes.gridContainer}`]: {
    flexGrow: 1,
    boxShadow: '0px 2px 7px 0px rgba(1, 16, 18, 0.15)',
  },
  [`& .${classes.grid}`]: {
    border: 'none',
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '143%',
    letterSpacing: '0.17px',

    '& .MuiDataGrid-virtualScrollerRenderZone': {
      zIndex: 1000,
      paddingTop: 8,
    },

    '& .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeaderTitleContent, & .MuiDataGrid-columnHeaderTitleContainerContent, & .MuiDataGrid-columnHeaderTitle':
      {
        width: '100%',
        color: 'rgba(1, 16, 18, 0.90)',

        fontFamily: 'Source Sans Pro',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '24px',
        letterSpacing: '0.17px',
      },

    // '& .MuiDataGrid-row--borderBottom .MuiDataGrid-columnHeader': {
    //   borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    // },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },

    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },

    '& .MuiDataGrid-cell': {
      '&.MuiDataGrid-cellCheckbox': {
        overflow: 'visible',

        '& .MuiButtonBase-root': {
          padding: '8px',
          marginLeft: '-6px',
          '& .MuiSvgIcon-root': {
            width: '24px !important',
            height: '24px !important',
          },
        },

        '& .MuiCheckbox-root.Mui-disabled': {
          position: 'relative',
          overflow: 'visible',
          pointerEvents: 'auto',
          cursor: 'pointer',
        },

        '& .MuiCheckbox-root.Mui-disabled::before': {
          content: `'Add missing trip info'`,
          position: 'absolute',
          fontFamily: 'Source Sans Pro',
          fontStyle: 'normal',
          fontSize: 10,
          fontWeight: 600,
          lineHeight: '14px',
          bottom: 36,
          left: 8,
          background: 'rgba(1, 16, 18, 0.90)',
          color: 'white',
          padding: '4px 8px',
          borderRadius: 4,
          boxShadow:
            '0px 5px 5px -3px rgba(0, 0, 0, 0.10), 0px 8px 10px 1px rgba(0, 0, 0, 0.07), 0px 3px 14px 2px rgba(0, 0, 0, 0.06)',

          opacity: 0,
          visibility: 'hidden',
          transition: 'opacity 0.3s ease-in-out',
        },

        '& .MuiCheckbox-root.Mui-disabled::after': {
          content: `''`,
          position: 'absolute',
          bottom: 22,
          left: 14,
          borderWidth: 6,
          borderTopWidth: 8,
          borderStyle: 'solid',
          borderColor:
            'rgba(1, 16, 18, 0.90) transparent transparent transparent',
          opacity: 0,
          visibility: 'hidden',
          transition: 'opacity 0.3s ease-in-out',
        },

        '& .MuiCheckbox-root.Mui-disabled:hover::before': {
          opacity: 1,
          visibility: 'visible',
        },
        '& .MuiCheckbox-root.Mui-disabled:hover::after': {
          opacity: 1,
          visibility: 'visible',
        },
      },
    },

    '& .MuiDataGrid-columnHeaderCheckbox': {
      width: '32px !important',

      // '& div': {
      //   display: 'none',
      // },
    },

    '& .MuiDataGrid-cellCheckbox': {
      '--width': '44px !important',
      marginRight: '-10px !important',
    },

    // I'm not sure why this was here but it causes console errors. I can't see any
    // difference with it in or out.
    // '& .MuiDataGrid-cell:nth-last-child(2)': {
    //   flex: 1,
    // },
    // [`& .MuiDataGridPro-row--editing`]: {
    //   [`& .${classes.cellContent} fieldset`]: {
    //     display: 'block !important',
    //   },
    // },
    '.MuiDataGrid-cell.MuiDataGrid-cell--editing:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-row': {
      '& .MuiDataGrid-cell:last-child button': {
        visibility: 'hidden',
      },
      [`& input::placeholder`]: {
        color: 'transparent',
      },
      [`& fieldset`]: {
        display: 'none',
      },
    },
    [`& .MuiDataGrid-row:hover, & .MuiDataGrid-row.${classes.rowHover}`]: {
      '& .MuiDataGrid-cell:last-child button': {
        visibility: 'visible',
      },
      [`& input::placeholder`]: {
        color: 'rgba(1, 16, 18, 0.60)',
      },
      [`& fieldset`]: {
        display: 'inherit',
      },
    },
    '.MuiDataGrid-cell.MuiDataGrid-cell--editing': {
      boxShadow: 'none',
      // backgroundColor: '#fff',
      '& fieldset': {
        display: 'block',
      },
    },
    // [`& .MuiDataGrid-row:not(:hover):not(.${classes.rowHover})`]: {
    //   '& .MuiDataGrid-cell:last-child button': {
    //     visibility: 'hidden',
    //   },

    //   [`& input::placeholder`]: {
    //     color: 'transparent',
    //   },
    //   [`& fieldset`]: {
    //     display: 'none',
    //   },
    // },
    [`& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.${classes.rowHover}, & .MuiDataGrid-row:hover`]:
      {
        '& .MuiDataGrid-row:focus-within': {
          [`& input::placeholder`]: {
            color: 'rgba(1, 16, 18, 0.60)',
            opacity: 1,
          },
          [`& fieldset`]: {
            display: 'inherit',
          },
        },
      },
    [`& .MuiDataGrid-row.${classes.rowHover}, & .MuiDataGrid-row:hover`]: {
      background:
        'linear-gradient(0deg, rgba(1, 16, 18, 0.04) 0%, rgba(1, 16, 18, 0.04) 100%), #FFF',
      ['& .MuiFormControl-root']: {
        backgroundColor: '#fff',
      },
    },
  },
  [`& .${classes.cellContent}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
    padding: theme.spacing(1),
  },
  [`& .${classes.primaryText}`]: {
    color: 'rgba(1, 16, 18, 0.90)',
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '143%',
    letterSpacing: '0.17px',
  },
  [`& .${classes.secondaryText}`]: {
    color: 'rgba(1, 16, 18, 0.60)',
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '143%',
  },

  [`& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-input`]:
    {
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '166%',
      letterSpacing: '0.4px',
    },

  [`& .MuiTablePagination-selectLabel`]: {
    color: 'rgba(1, 16, 18, 0.60)',
  },
  [`& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows`]: {
    fontFamily: 'Source Sans Pro',
  },
  [`& .MuiTablePagination-input`]: {
    fontFamily: 'Roboto',

    [`& .MuiSelect-select`]: {
      paddingBottom: 0,
    },
  },
  [`& .${classes.renderTime}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: theme.spacing(4),
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '143%',
    textAlign: 'center !important',
    width: '100%',
    '& input': {
      paddingLeft: 0,
      color: 'rgba(1, 16, 18, 0.90)',
      letterSpacing: '0.17px',
      textAlign: 'center',
      padding: '6px',
    },
    '& fieldset': {
      display: 'block',
    },
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  [`& .MuiPaper-root`]: {
    width: '160px',
    marginLeft: '10px',
    borderRadius: '3px',
    boxShadow:
      '0px 5px 5px -3px rgba(0, 0, 0, 0.10), 0px 8px 10px 1px rgba(0, 0, 0, 0.07), 0px 3px 14px 2px rgba(0, 0, 0, 0.06)',

    [`& .MuiList-root`]: {
      padding: 0,
    },
    [`& .MuiMenuItem-root`]: {
      color: 'rgba(1, 16, 18, 0.60)',
      fontFamily: 'Source Sans Pro',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '143%',
      letterSpacing: '0.17px',
      padding: '8px 16px',
    },
  },
  [`& .${classes.actions}`]: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,
    gap: theme.spacing(2),
    padding: theme.spacing(1),
    alignItems: 'flex-start',
  },
}));

interface AttestTripsTableProps {
  attestableTripLegs: PagedList<TripLegSearchResponseItem> | null;
  onTripsUpdated: (tripLegs: TripLegSearchResponseItem[]) => void;
  onSubmit: () => void;
  loading: boolean;
  providerId: number | null | undefined;
}

const AttestTripsTable = ({
  attestableTripLegs,
  onTripsUpdated,
  onSubmit,
  loading,
  providerId,
}: AttestTripsTableProps) => {
  const { t } = useTranslation('common');
  const apiRef = useGridApiRef();
  const dispatch = useAppDispatch();
  const isInvalidNoLoadTime = useIsInvalidNoLoadTime();

  const [moreActionsAnchorEl, setMoreActionsAnchorEl] =
    useState<null | HTMLElement>(null);
  const [moreActionsMenuOpenRowId, setMoreActionsMenuOpenRowId] = useState<
    string | null
  >(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [originalStatus, setOriginalStatus] = useState<TripLegStatusBC | null>(
    null,
  );
  const [targetStatus, setTargetStatus] = useState<TripLegStatus>(
    TripLegStatus.CanceledLate,
  );
  const [selectedTrips, setSelectedTrips] = useState<string[]>([]);
  const [cellResetCounters, setCellResetCounters] = useState<
    Map<string, number>
  >(new Map());
  const [selectedTripLeg, setSelectedTripLeg] =
    useState<TripLegAttestationItem | null>(null);
  // Driver name is the only field the BE doesn't roundtrip during attestation,
  // so we keep user-entered drivers in a local map keyed by tripLegReferenceID
  // and overlay them when building rows.
  const [driverNameOverrides, setDriverNameOverrides] = useState<
    Record<string, string>
  >({});

  const timezones = useAppSelector(selectTimeZones);
  const updatingTripLegLogID = useAppSelector(selectUpdatingTripLegLogID);
  const updatingTripLegLogStatus = useAppSelector(
    selectUpdatingTripLegLogStatus,
  );
  const tripLegError = useAppSelector(selectTripLegError);
  const tripLegPriceCalculationError = useAppSelector(
    selectTripLegPriceCalculationError,
  );
  const priceCalculationErrorMessage = tripLegPriceCalculationError
    ? t('needsAttention.priceCouldNotBeCalculated')
    : null;
  const tripLegBannerError = priceCalculationErrorMessage ?? tripLegError;

  // Filter Will Call trips that haven't been activated (no pickup time).
  const filteredItems = useMemo(
    () =>
      attestableTripLegs?.items.filter(
        (tripLeg) => !(tripLeg.isWillCall && !tripLeg.pickUpDueTimeUTC),
      ) ?? [],
    [attestableTripLegs],
  );

  const {
    optimisticTimestamps,
    optimisticStatuses,
    updateOptimisticTimestamp,
    updateOptimisticStatus,
    clearOptimisticTimestampsForTrip,
  } = useOptimisticUpdates(filteredItems);

  // Refs let the rollback effect know which trip + which logs were optimistically
  // committed during the most recent edit so they can be unwound on backend error.
  const lastOptimisticTripRef = useRef<string | null>(null);
  const lastCommittedLogsRef = useRef<
    { tripLegReferenceID: string; status: TripLegStatusBC }[]
  >([]);

  const checkTimestampsFilled = (tripLeg: TripLegAttestationItem) => {
    const status = tripLeg?.tripLegStatusBC;

    if (
      status === TripLegStatusBC.CanceledLate ||
      status === TripLegStatusBC.NoLoad ||
      status === TripLegStatusBC.Completed
    ) {
      return true;
    }

    return false;
  };

  const isRowValid = (tripLeg: TripLegAttestationItem | undefined) => {
    if (!tripLeg) return false;

    const isValidPayableAmount = Boolean(tripLeg.payableAmount);
    const isCompleteState = tripStatusCompletedList.includes(
      tripLeg.tripLegStatusBC as TripLegStatusBC,
    );

    // driver is not needed for CanceledLate
    const isValidDriver =
      tripLeg.tripLegStatusBC === TripLegStatusBC.CanceledLate
        ? true
        : Boolean(tripLeg.driverName);

    return isValidDriver && isValidPayableAmount && isCompleteState;
  };

  const getException = (tripLeg: TripLegAttestationItem) => {
    if (tripLeg.isUnderDiscussion) {
      return t('needsAttention.underDiscussion');
    }
    if (!isRowValid(tripLeg)) {
      return t('needsAttention.missingInfo');
    }
    return t('needsAttention.readyToInvoice');
  };

  const tripLegs = useMemo<TripLegAttestationItem[]>(() => {
    return filteredItems.map((tripLeg) => {
      const row = transformTripLegToRow(
        tripLeg,
        timezones,
        optimisticTimestamps,
        optimisticStatuses,
        t,
      ) as TripLegAttestationItem;

      const driverOverride = driverNameOverrides[tripLeg.tripLegReferenceID];
      if (driverOverride) {
        row.driverName = driverOverride;
      }
      row.hasRequiredTimestamps = checkTimestampsFilled(row);
      row.exception = getException(row);
      return row;
    });
  }, [
    filteredItems,
    timezones,
    optimisticTimestamps,
    optimisticStatuses,
    driverNameOverrides,
    t,
  ]);

  const updateTripsWithTimestamp = (
    tripLegReferenceID: string,
    status: TripLegStatusBC,
    timestampUTC: string,
    field: string,
  ) => {
    // Route the write to the optimistic-hook field name. For Canceled Late and
    // No Load rows the visible "On Site"/"On Board" cells render from the
    // canceledLate / noLoad logs, not the regular onSite / onBoard logs.
    let optimisticField = field;
    if (status === TripLegStatusBC.CanceledLate) {
      optimisticField = 'canceledLate';
    } else if (status === TripLegStatusBC.NoLoad) {
      optimisticField = 'noLoad';
    }

    if (lastOptimisticTripRef.current !== tripLegReferenceID) {
      lastCommittedLogsRef.current = [];
    }
    lastOptimisticTripRef.current = tripLegReferenceID;
    lastCommittedLogsRef.current = [
      ...lastCommittedLogsRef.current,
      { tripLegReferenceID, status },
    ];

    updateOptimisticTimestamp(
      tripLegReferenceID,
      optimisticField,
      timestampUTC,
    );
  };

  const isUpdatingCurrentTimeField = (
    tripLegReferenceID: string,
    field: string,
  ): boolean => {
    if (updatingTripLegLogID === tripLegReferenceID) {
      if (
        field === 'onSite' &&
        updatingTripLegLogStatus === TripLegStatusBC.Onsite
      ) {
        return true;
      }
      if (
        field === 'onBoard' &&
        updatingTripLegLogStatus === TripLegStatusBC.Onboard
      ) {
        return true;
      }
      if (
        field === 'completed' &&
        updatingTripLegLogStatus === TripLegStatusBC.Completed
      ) {
        return true;
      }
    }
    return false;
  };

  const openMoreActionsMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    rowId: string,
  ) => {
    setMoreActionsAnchorEl(event.currentTarget);
    setMoreActionsMenuOpenRowId(rowId);
  };

  const closeMoreActionsMenu = () => {
    setMoreActionsAnchorEl(null);
    setMoreActionsMenuOpenRowId(null);
  };

  const renderPickupTime = (params: GridCellParams): React.ReactElement => {
    const { row } = params;
    return (
      <CellDualValue
        primaryValue={row.pickupTime}
        secondaryValue={row.tripID}
      />
    );
  };

  const renderPickupAddress = (params: GridCellParams): React.ReactElement => {
    const { row } = params;

    return (
      <CellDualValue
        primaryValue={row.pickupAddress}
        secondaryValue={`${row.pickupCity}, ${row.pickupState}, ${row.pickupZipCode}`}
      />
    );
  };

  const renderDropoffAddress = (params: GridCellParams): React.ReactElement => {
    const { row } = params;
    return (
      <CellDualValue
        primaryValue={row.dropoffAddress}
        secondaryValue={`${row.dropoffCity}, ${row.dropoffState}, ${row.dropoffZipCode}`}
      />
    );
  };

  const renderDriver = (params: GridCellParams): React.ReactElement => {
    const tripLeg = params.row as TripLegAttestationItem;
    const driverName = tripLeg.driverName || '';
    return (
      <CellEditableField
        value={driverName}
        placeholder="Driver"
        defaultValue={driverName}
        hasFocus={params.hasFocus}
        align="left"
        onChange={(value) => {
          setDriverNameOverrides((prev) => {
            const next = { ...prev };
            if (typeof value === 'string' && value) {
              next[tripLeg.tripLegReferenceID] = value;
            } else {
              delete next[tripLeg.tripLegReferenceID];
            }
            return next;
          });
        }}
      />
    );
  };

  const renderTime = (params: GridCellParams): React.ReactElement => {
    const { row, field } = params;
    const time = params.value
      ? DateTime.fromISO(params.value as string, {
          zone: params.row.timezone,
        })
      : undefined;
    const canceledLateTime = row.canceledLateTimeStamp
      ? DateTime.fromISO(row.canceledLateTimeStamp, {
          zone: params.row.timezone,
        })
      : undefined;
    const noLoadTime = row.noLoadTimeStamp
      ? DateTime.fromISO(row.noLoadTimeStamp, {
          zone: params.row.timezone,
        })
      : undefined;
    const tripLegStatus = row.tripLegStatusBC;

    const getLabel = (): string | null => {
      if (
        tripLegStatus === TripLegStatusBC.NoLoad &&
        noLoadTime &&
        field === 'onBoard'
      ) {
        return t('tripLegStatus.noLoad');
      }
      if (
        tripLegStatus === TripLegStatusBC.CanceledLate &&
        canceledLateTime &&
        field === 'onSite'
      ) {
        return t('tripLegStatus.canceledLate');
      }
      return null;
    };

    if (
      field === 'completed' &&
      (tripLegStatus === TripLegStatusBC.CanceledLate ||
        tripLegStatus === TripLegStatusBC.NoLoad)
    ) {
      return <></>;
    }

    if (field === 'onBoard' && tripLegStatus === TripLegStatusBC.CanceledLate) {
      return <></>;
    }

    const isUpdating = isUpdatingCurrentTimeField(
      params.row.tripLegReferenceID,
      field,
    );

    const label = getLabel();

    let valueToDisplay: DateTime | undefined;

    if (
      field === 'onSite' &&
      tripLegStatus === TripLegStatusBC.CanceledLate &&
      canceledLateTime
    ) {
      valueToDisplay = canceledLateTime;
    } else if (
      field === 'onBoard' &&
      tripLegStatus === TripLegStatusBC.NoLoad
    ) {
      valueToDisplay = noLoadTime;
    } else {
      valueToDisplay = time;
    }
    return (
      <Box className={classes.cellContent}>
        <Typography className={classes.primaryText}>
          {label && (
            <Typography className={classes.primaryText} sx={{ marginTop: 1 }}>
              {label}
            </Typography>
          )}
        </Typography>
        <Typography
          className={`${classes.renderTime} ${label ? 'has-label' : ''}`}>
          <TimeFieldCell
            key={`${params.row.tripLegReferenceID}-${field}-${
              cellResetCounters.get(
                `${params.row.tripLegReferenceID}-${field}`,
              ) ?? 0
            }`}
            value={valueToDisplay}
            onBlur={(event) => {
              const newTime = event.target.value;
              const isPlaceholder = newTime === 'hh:mm aa';
              const hasTimeEntered = newTime && !isPlaceholder;

              // Skip update if value hasn't changed
              const currentFormatted = valueToDisplay
                ? valueToDisplay.toFormat('hh:mm a')
                : '';
              if (
                newTime === currentFormatted ||
                (!hasTimeEntered && !valueToDisplay)
              ) {
                return;
              }

              // Clear any existing error when the user enters a new time
              if (hasTimeEntered) {
                dispatch(setTripLegError(null));
              }

              const resetField = () => {
                setCellResetCounters((prev) => {
                  const next = new Map(prev);
                  const cellKey = `${params.row.tripLegReferenceID}-${field}`;
                  next.set(cellKey, (prev.get(cellKey) ?? 0) + 1);
                  return next;
                });
              };

              // Validate that previous status is filled before allowing update
              // Only show error if user actually entered a time
              if (hasTimeEntered && field === 'onBoard' && row.onSite === '') {
                resetField();
                dispatch(
                  setTripLegError(t('needsAttention.onSiteRequiredFirst')),
                );
                return;
              }
              if (
                hasTimeEntered &&
                field === 'completed' &&
                (row.onSite === '' || row.onBoard === '')
              ) {
                resetField();
                dispatch(
                  setTripLegError(
                    t('needsAttention.previousTimestampsRequired'),
                  ),
                );
                return;
              }

              // Validate chronological order for regular trips
              if (
                hasTimeEntered &&
                tripLegStatus !== TripLegStatusBC.NoLoad &&
                tripLegStatus !== TripLegStatusBC.CanceledLate
              ) {
                const date = params.row.appointmentDate;
                const zone = params.row.timezone;
                const newDateTime = DateTime.fromFormat(
                  `${date} ${newTime}`,
                  'yyyy-MM-dd h:mm a',
                  { zone },
                );

                if (field === 'onBoard' && row.onSite) {
                  const onSiteDateTime = DateTime.fromISO(row.onSite, {
                    zone,
                  });
                  if (
                    newDateTime.isValid &&
                    onSiteDateTime.isValid &&
                    newDateTime < onSiteDateTime
                  ) {
                    resetField();
                    dispatch(
                      setTripLegError(t('needsAttention.onBoardBeforeOnSite')),
                    );
                    return;
                  }
                }

                if (field === 'completed' && row.onBoard) {
                  const onBoardDateTime = DateTime.fromISO(row.onBoard, {
                    zone,
                  });
                  if (
                    newDateTime.isValid &&
                    onBoardDateTime.isValid &&
                    newDateTime < onBoardDateTime
                  ) {
                    resetField();
                    dispatch(
                      setTripLegError(
                        t('needsAttention.completedBeforeOnBoard'),
                      ),
                    );
                    return;
                  }
                }
              }

              // Validate NoLoad time is not earlier than OnSite time
              if (hasTimeEntered && tripLegStatus === TripLegStatusBC.NoLoad) {
                const date = params.row.appointmentDate;
                const zone = params.row.timezone;
                const newDateTime = DateTime.fromFormat(
                  `${date} ${newTime}`,
                  'yyyy-MM-dd h:mm a',
                  { zone },
                );

                if (field === 'onBoard' && row.onSite) {
                  const onSiteDateTime = DateTime.fromISO(row.onSite, {
                    zone,
                  });
                  const pickupDueDateTime = DateTime.fromISO(
                    row.pickUpDueTimeUTC || row.appointmentTimeUTC || '',
                    { setZone: true },
                  );

                  const { isInvalid, errorMessage } = isInvalidNoLoadTime(
                    newDateTime,
                    onSiteDateTime,
                    pickupDueDateTime,
                  );
                  if (isInvalid) {
                    resetField();
                    dispatch(setTripLegError(errorMessage));
                    return;
                  }
                }

                if (field === 'onSite' && noLoadTime) {
                  const pickupDueDateTime = DateTime.fromISO(
                    row.pickUpDueTimeUTC || row.appointmentTimeUTC || '',
                    { setZone: true },
                  );

                  const { isInvalid, errorMessage } = isInvalidNoLoadTime(
                    noLoadTime,
                    newDateTime,
                    pickupDueDateTime,
                  );
                  if (isInvalid) {
                    resetField();
                    dispatch(setTripLegError(errorMessage));
                    return;
                  }
                }
              }

              const date = params.row.appointmentDate;
              let fieldName = params.field.toUpperCase();
              if (field === 'onSite' && canceledLateTime) {
                fieldName =
                  TripLegStatusBC.CanceledLate.toString().toUpperCase();
              }
              if (
                field === 'onBoard' &&
                (noLoadTime || tripLegStatus === TripLegStatusBC.NoLoad)
              ) {
                fieldName = TripLegStatusBC.NoLoad.toString().toUpperCase();
              }
              const newDateTime = DateTime.fromFormat(
                `${date} ${newTime}`,
                'yyyy-MM-dd h:mm a',
                { zone: params.row.timezone },
              ).toUTC();
              const isSame =
                newDateTime.startOf('minute').toISO() ===
                valueToDisplay?.startOf('minute').toISO();
              if (newDateTime.isValid && !isSame && !isPlaceholder) {
                updateTripsWithTimestamp(
                  params.row.tripLegReferenceID,
                  fieldName as TripLegStatusBC,
                  newDateTime.toISO()!,
                  field,
                );
                const timestampExists = !!attestableTripLegs?.items.find(
                  (tl) =>
                    tl.tripLegReferenceID === params.row.tripLegReferenceID &&
                    tl.tripLegStatusLogs?.find(
                      (log) => log.tripLegStatusBC === fieldName,
                    ),
                );

                if (fieldName === TripLegStatusBC.Completed) {
                  dispatch(
                    overrideTripLegStatus(
                      params.row.passengerUUID,
                      params.row.tripLegReferenceID,
                      tripLegStatusBCToStatusMap[fieldName as TripLegStatusBC]!,
                      newDateTime.toISO(),
                      providerId,
                    ),
                  );
                }

                dispatch(
                  updateTripLegLogAsync(
                    fieldName as TripLegStatusBC,
                    params.row.passengerUUID,
                    params.row.tripLegReferenceID,
                    newDateTime.setZone('utc').toISO()!,
                    providerId,
                    timestampExists,
                    params.row.tripLegStatusBC,
                  ),
                );
              }
            }}
          />
        </Typography>
        {isUpdating && <LinearProgress />}
      </Box>
    );
  };

  const renderException = (params: GridCellParams): React.ReactElement => {
    const { row } = params;
    return <Box className={classes.cellContent}>{getException(row)}</Box>;
  };

  const renderCenterHeader = ({
    colDef: { headerName },
  }: {
    colDef: { headerName?: string };
  }): React.ReactNode => {
    return (
      <Box
        style={{
          textAlign: 'center',
          width: '100%',
          lineHeight: '100%',
          height: '1',
        }}>
        {headerName}
      </Box>
    );
  };

  const renderPrice = (params: GridCellParams): React.ReactElement => {
    return <PriceTooltip params={params as any} classes={classes} />;
  };

  const isRowSelectable = (params: GridRowParams<TripLegAttestationItem>) => {
    const { row } = params;
    return isRowValid(row);
  };

  const columns: GridColDef<TripLegAttestationItem>[] = [
    {
      field: 'pickupTime',
      headerName: t('needsAttention.pickup'),
      flex: 1,
      renderCell: renderPickupTime,
    },
    {
      field: 'pickupAddress',
      headerName: t('needsAttention.pickupAddress'),
      flex: 1,
      renderCell: renderPickupAddress,
    },
    {
      field: 'dropoffAddress',
      headerName: t('needsAttention.dropoffAddress'),
      flex: 1,
      renderCell: renderDropoffAddress,
    },
    {
      field: 'driverName',
      headerName: t('needsAttention.driver'),
      width: 140,
      renderCell: renderDriver,
    },
    {
      field: 'onSite',
      headerName: t('tripLegStatus.onSite'),
      renderCell: renderTime,
      width: 120,
      renderHeader: (params) => renderCenterHeader(params),
      sortable: false,
    },
    {
      field: 'onBoard',
      headerName: t('tripLegStatus.onBoard'),
      renderCell: renderTime,
      width: 120,
      align: 'center',
      renderHeader: (params) => renderCenterHeader(params),
      sortable: false,
    },
    {
      field: 'completed',
      headerName: t('tripLegStatus.completed'),
      renderCell: renderTime,
      width: 120,
      align: 'center',
      renderHeader: (params) => renderCenterHeader(params),
      sortable: false,
    },
    {
      field: 'price',
      headerName: t('needsAttention.price'),
      width: 70,
      renderCell: renderPrice,
    },
    {
      field: 'exception',
      headerName: t('needsAttention.exception'),
      width: 120,
      renderCell: renderException,
    },
    {
      field: 'actions',
      headerName: ' ',
      width: 40,
      sortable: false,
      renderCell: (params) => (
        <Box className={classes.cellContent}>
          <IconButton
            onClick={(e) =>
              openMoreActionsMenu(e, params.row.tripLegReferenceID)
            }>
            <MoreVertIcon />
          </IconButton>
          <StyledMenu
            anchorEl={moreActionsAnchorEl}
            open={
              Boolean(moreActionsAnchorEl) &&
              moreActionsMenuOpenRowId === params.row.tripLegReferenceID
            }
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            onClose={closeMoreActionsMenu}
            elevation={1}>
            <MenuItem
              disabled={params.row.tripLegStatusBC === TripLegStatusBC.NoLoad}
              onClick={() => {
                setSelectedTripLeg(params.row);
                setOriginalStatus(
                  params.row.tripLegStatusBC as TripLegStatusBC,
                );
                setTargetStatus(TripLegStatus.NoLoad);
                setStatusDialogOpen(true);
                closeMoreActionsMenu();
              }}>
              {t('tripLegStatus.noLoad')}
            </MenuItem>
            <MenuItem
              disabled={
                params.row.tripLegStatusBC === TripLegStatusBC.CanceledLate
              }
              onClick={() => {
                setSelectedTripLeg(params.row);
                setOriginalStatus(
                  params.row.tripLegStatusBC as TripLegStatusBC,
                );
                setTargetStatus(TripLegStatus.CanceledLate);
                setStatusDialogOpen(true);
                closeMoreActionsMenu();
              }}>
              {t('tripLegStatus.canceledLate')}
            </MenuItem>
            <MenuItem
              disabled={
                params.row.tripLegStatusBC !== TripLegStatusBC.CanceledLate &&
                params.row.tripLegStatusBC !== TripLegStatusBC.NoLoad
              }
              onClick={() => {
                setSelectedTripLeg(params.row);
                setOriginalStatus(
                  params.row.tripLegStatusBC as TripLegStatusBC,
                );
                setTargetStatus(TripLegStatus.Completed);
                setStatusDialogOpen(true);
                closeMoreActionsMenu();
              }}>
              {t('tripLegStatus.completed')}
            </MenuItem>
          </StyledMenu>
        </Box>
      ),
    },
  ];

  const handleSubmit = () => {
    const selectedTripLegs = tripLegs.filter((tripLeg) => {
      if (tripLeg.tripLegReferenceID) {
        return selectedTrips.includes(tripLeg.tripLegReferenceID);
      }
      return false;
    });
    onTripsUpdated(selectedTripLegs);
    if (selectedTripLegs.length > 0) {
      onSubmit();
    }
  };

  // Roll back optimistic state on backend error so the row reverts to whatever
  // Redux currently has. Mirrors the pattern in NeedsAttentionDialog.
  useEffect(() => {
    if (tripLegError && lastOptimisticTripRef.current) {
      const trippedRef = lastOptimisticTripRef.current;
      clearOptimisticTimestampsForTrip(trippedRef);
      lastCommittedLogsRef.current.forEach(({ tripLegReferenceID, status }) => {
        dispatch(removeTripLegLogEntry({ tripLegReferenceID, status }));
      });
      setCellResetCounters((prev) => {
        const next = new Map(prev);
        ['onSite', 'onBoard', 'completed'].forEach((f) => {
          const key = `${trippedRef}-${f}`;
          next.set(key, (prev.get(key) ?? 0) + 1);
        });
        return next;
      });
      lastCommittedLogsRef.current = [];
      lastOptimisticTripRef.current = null;
    }
    // dispatch and clearOptimisticTimestampsForTrip are stable references.
  }, [tripLegError]);

  return (
    <StyledBox className={classes.content}>
      <Paper elevation={3} className={classes.gridContainer}>
        <DataGridPro
          getRowId={(row) => row.tripLegReferenceID}
          rows={tripLegs}
          columns={columns}
          apiRef={apiRef}
          getRowClassName={(params) => {
            if (params.row.tripLegReferenceID === moreActionsMenuOpenRowId) {
              return classes.rowHover;
            }
            return '';
          }}
          className={classes.grid}
          checkboxSelection
          isRowSelectable={isRowSelectable}
          disableRowSelectionOnClick
          rowSelectionModel={selectedTrips}
          onRowSelectionModelChange={(model) => {
            setSelectedTrips(model as string[]);
          }}
          hideFooterSelectedRowCount
          rowHeight={56}
          disableColumnMenu={true}
          disableColumnSorting={true}
          loading={loading}
          // editMode="row"
          // hideFooter
          // cellModesModel={cellModesModel}
          // onCellModesModelChange={handleCellModesModelChange}
          // onCellClick={handleCellClick}
        />
      </Paper>
      <Box className={classes.actions}>
        <Button
          variant="contained"
          color="primary"
          fullWidth={false}
          disabled={!selectedTrips.length}
          onClick={handleSubmit}>
          {t('common.submit')}
        </Button>
      </Box>
      <StatusDialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        selectedTripLeg={selectedTripLeg}
        providerId={providerId ?? 0}
        originalStatus={originalStatus ?? null}
        targetStatus={targetStatus}
        onStatusChanged={(tripLegReferenceID, newStatus, timestamps) => {
          // Track the trip and any logs the dialog will commit so the
          // tripLegError effect knows what to roll back if the BE call fails.
          lastOptimisticTripRef.current = tripLegReferenceID;
          const committedStatuses: TripLegStatusBC[] = [];
          if (timestamps.onSite) committedStatuses.push(TripLegStatusBC.Onsite);
          if (timestamps.onBoard) {
            committedStatuses.push(TripLegStatusBC.Onboard);
          }
          if (timestamps.completed) {
            committedStatuses.push(TripLegStatusBC.Completed);
          }
          if (timestamps.noLoad) committedStatuses.push(TripLegStatusBC.NoLoad);
          if (timestamps.canceledLate) {
            committedStatuses.push(TripLegStatusBC.CanceledLate);
          }
          lastCommittedLogsRef.current = committedStatuses.map((status) => ({
            tripLegReferenceID,
            status,
          }));
          updateOptimisticStatus(tripLegReferenceID, newStatus, timestamps);
        }}
      />
      <Snackbar
        open={!!tripLegBannerError}
        onClose={() => {
          dispatch(setTripLegError(null));
          dispatch(setTripLegPriceCalculationError(false));
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert variant="filled" severity="error">
          {tripLegBannerError}
        </Alert>
      </Snackbar>
    </StyledBox>
  );
};

export { AttestTripsTable };
